import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Pause, ShieldCheck, Info, Circle, CheckCircle2, Copy } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { Card, Spinner, useToast } from "@/components/earnos/kit";

import GoalExecution from "@/components/earnos/GoalExecution";

const LEVELS = [
  { key: "SUGGEST", label: "Suggest", desc: "EARNOS only recommends actions." },
  { key: "PREPARE", label: "Prepare", desc: "EARNOS drafts, you review and send." },
  { key: "APPROVE_EACH_TIME", label: "Approve each time", desc: "Drafts require your review before they are ready to send." },
  { key: "AUTOPILOT_WITHIN_RULES", label: "Autopilot within rules", desc: "Saved rules for future connected actions. Automatic sending is not connected." },
];

export default function Autopilot() {
  const navigate = useNavigate();
  const toast = useToast();
  const [rules, setRules] = useState(null);
  const [events, setEvents] = useState([]);
  const [note, setNote] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [edited, setEdited] = useState({});
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch("/autopilot");
      setRules(d.rules); setEvents(d.events || []); setNote(d.note); setDrafts(d.drafts || []); setLeads(d.leads || []);
    } catch (e) { toast.show(e?.message || "Failed", "error"); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const setLevel = async (level) => {
    const next = { ...rules, permission_level: level };
    setBusy("rules");
    try {
      await apiFetch("/autopilot", { method: "PUT", body: next });
      setRules(next);
      toast.show("Permission saved", "success");
    } catch (e) { toast.show(e?.message || "Could not save permission", "error"); }
    finally { setBusy(null); }
  };

  const pauseAll = async () => {
    setBusy("pause");
    try {
      await apiFetch("/autopilot/pause", { method: "POST" });
      toast.show("All automation paused", "success");
      await load();
    } catch (e) { toast.show(e?.message || "Could not pause", "error"); }
    finally { setBusy(null); }
  };

  const prepare = async (leadId) => {
    setBusy(leadId);
    try {
      await apiFetch(`/autopilot/prepare/${encodeURIComponent(leadId)}`, { method: "POST" });
      toast.show("Draft ready for review", "success");
      await load();
    } catch (e) { toast.show(e?.message || "Could not prepare draft", "error"); }
    finally { setBusy(null); }
  };

  const review = async (draft, decision) => {
    setBusy(draft.action_id);
    try {
      await apiFetch(`/autopilot/review/${encodeURIComponent(draft.action_id)}`, {
        method: "POST", body: { decision, message: edited[draft.action_id] ?? draft.message },
      });
      toast.show(decision === "approve" ? "Approved for you to copy; not sent" : "Draft declined", "success");
      await load();
    } catch (e) { toast.show(e?.message || "Could not review draft", "error"); }
    finally { setBusy(null); }
  };

  const copy = async (draft) => {
    try { await navigator.clipboard.writeText(draft.message); toast.show("Draft copied", "success"); }
    catch { toast.show("Select and copy the message text below", "error"); }
  };

  if (loading) return <Spinner className="py-24" />;

  return (
    <div className="animate-fade-in max-w-3xl">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate(-1)} data-testid="ap-back" className="text-ink lg:hidden"><ChevronLeft size={26} /></button>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink">Autopilot</h1>
      </div>

      <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface-2 p-5">
        <div className="flex-1">
          <p className="text-lg font-bold text-ink">Master control</p>
          <p className="text-sm text-ink-2 mt-0.5">Automatic sending is not connected. Prepare and review drafts below.</p>
        </div>
        <button onClick={pauseAll} disabled={!!busy} data-testid="pause-autopilot" className="inline-flex items-center gap-1.5 h-11 px-4 rounded-full bg-danger text-white font-bold hover:brightness-110"><Pause size={16} /> Pause all</button>
      </div>

      <GoalExecution />

      <Card className="mt-5">
        <h2 className="text-lg font-bold text-ink">Needs you</h2>
        <p className="text-sm text-ink-2 mt-1">Prepare an editable follow-up from a saved lead. Uses your plan&apos;s AI allowance.</p>
        {leads.length === 0 ? <p className="text-sm text-ink-2 mt-3">Save a lead to prepare your first follow-up.</p> : leads.map((lead) => (
          <div key={lead.lead_id} className="flex items-center gap-3 mt-3">
            <span className="flex-1 text-ink">{lead.business_name}</span>
            <button disabled={!!busy || drafts.some((d) => d.lead_id === lead.lead_id && ["needs_review", "approved"].includes(d.status))} onClick={() => prepare(lead.lead_id)} className="rounded-full bg-brand px-3 py-2 text-sm font-bold text-white disabled:opacity-50">{busy === lead.lead_id ? "Preparing…" : "Draft follow-up"}</button>
          </div>
        ))}
        <button onClick={() => navigate("/leads")} className="mt-3 text-sm font-bold text-brand-light">Manage leads</button>
      </Card>

      {drafts.map((draft) => (
        <Card key={draft.action_id} className="mt-4">
          <p className="text-base font-bold text-ink">Follow-up · {draft.business_name}</p>
          <p className="text-sm text-ink-2 mt-1">{draft.status === "needs_review" ? "Review and edit before approving." : draft.status === "approved" ? "Approved. Ready for you to send; not sent automatically." : "Declined."}</p>
          <textarea aria-label={`Follow-up for ${draft.business_name}`} rows={5} maxLength={6000} readOnly={draft.status !== "needs_review" || !!busy} value={draft.status === "needs_review" ? (edited[draft.action_id] ?? draft.message) : draft.message} onChange={(e) => setEdited((prev) => ({ ...prev, [draft.action_id]: e.target.value }))} className="mt-3 w-full rounded-xl border border-line bg-surface p-3 text-base text-ink" />
          {draft.status === "needs_review" ? (
            <div className="flex gap-3 mt-3">
              <button data-testid={`approve-${draft.action_id}`} disabled={!!busy || !(edited[draft.action_id] ?? draft.message).trim()} onClick={() => review(draft, "approve")} className="rounded-full bg-brand px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Approve draft</button>
              <button disabled={!!busy} onClick={() => review(draft, "decline")} className="rounded-full border border-line px-4 py-2 text-sm text-ink-2">Decline</button>
            </div>
          ) : draft.status === "approved" ? (
            <button onClick={() => copy(draft)} className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-bold text-white"><Copy size={15} />Copy approved draft</button>
          ) : null}
        </Card>
      ))}

      <h3 className="text-lg font-bold text-ink mt-6">Permission level</h3>
      <div className="space-y-3 mt-3">
        {LEVELS.map((l) => {
          const active = rules?.permission_level === l.key;
          return (
            <button key={l.key} onClick={() => setLevel(l.key)} disabled={!!busy} data-testid={`level-${l.key}`} className={`w-full flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${active ? "border-brand bg-brand/10" : "border-line bg-surface-2 hover:border-line-strong"}`}>
              {active ? <CheckCircle2 size={20} className="text-brand-light mt-0.5 shrink-0" /> : <Circle size={20} className="text-ink-2 mt-0.5 shrink-0" />}
              <div><p className="text-lg font-bold text-ink">{l.label}</p><p className="text-sm text-ink-2 mt-0.5 leading-relaxed">{l.desc}</p></div>
            </button>
          );
        })}
      </div>

      <Card className="mt-6">
        <p className="text-base font-bold text-ink mb-2">EARNOS will never do without stronger confirmation</p>
        {["Move or transfer money", "Change pricing", "Accept contracts", "Delete your data", "Share sensitive information"].map((g) => (
          <div key={g} className="flex items-center gap-2 mt-1.5"><ShieldCheck size={15} className="text-ok shrink-0" /><span className="text-base text-ink-3">{g}</span></div>
        ))}
      </Card>

      <h3 className="text-lg font-bold text-ink mt-6">Action log</h3>
      {events.length === 0 ? (
        <p className="text-base text-ink-2 mt-3 leading-relaxed">No automated actions yet. Everything EARNOS prepares is logged here with the permission used.</p>
      ) : (
        <div className="space-y-2 mt-3">
          {events.map((e) => (
            <div key={e.action_id} className="flex gap-3 rounded-xl border border-line bg-surface-2 p-3">
              <span className="self-start px-2 py-0.5 rounded bg-brand/20 text-brand-light text-sm font-bold">{e.agent}</span>
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-ink">{e.action}</p>
                <p className="text-sm text-ink-2">{e.reason} · {e.result}</p>
                <p className="text-sm text-brand-light mt-0.5">Permission: {e.permission_used}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {note ? (
        <div className="flex items-start gap-1.5 mt-8"><Info size={14} className="text-ink-2 mt-0.5 shrink-0" /><p className="text-sm text-ink-2 leading-tight">{note}</p></div>
      ) : null}
    </div>
  );
}
